/**
 * Connection Health Worker
 * 
 * This Web Worker runs in a separate thread that is less throttled by browsers
 * when the tab is in the background. It sends periodic heartbeat pings to the
 * main thread to check connection health.
 * 
 * Usage: Import createConnectionWorker and call it to get a worker instance
 */

// Worker code as a string (will be converted to Blob URL)
const workerCode = `
  let heartbeatInterval = null;
  let lastPongTime = Date.now();
  let missedPongs = 0;
  const MAX_MISSED_PONGS = 3;
  const HEARTBEAT_INTERVAL = 10000; // 10 seconds (less than browser throttle limit)
  const PONG_TIMEOUT = 5000; // 5 seconds to wait for pong

  // Start heartbeat when worker initializes
  function startHeartbeat() {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }
    
    missedPongs = 0;
    lastPongTime = Date.now();
    
    heartbeatInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastPong = now - lastPongTime;
      
      // Check if we missed too many pongs (main thread not responding)
      if (timeSinceLastPong > HEARTBEAT_INTERVAL + PONG_TIMEOUT) {
        missedPongs++;
        console.log('[Worker] Missed pong #' + missedPongs + ', time since last: ' + timeSinceLastPong + 'ms');
        
        if (missedPongs >= MAX_MISSED_PONGS) {
          console.log('[Worker] Too many missed pongs, signaling reconnect needed');
          self.postMessage({ type: 'RECONNECT_NEEDED', reason: 'MAIN_THREAD_UNRESPONSIVE' });
          missedPongs = 0; // Reset after signaling
        }
      }
      
      // Send ping to main thread
      self.postMessage({ 
        type: 'PING', 
        timestamp: now,
        missedPongs: missedPongs
      });
    }, HEARTBEAT_INTERVAL);
    
    console.log('[Worker] Heartbeat started with interval: ' + HEARTBEAT_INTERVAL + 'ms');
  }

  // Handle messages from main thread
  self.onmessage = function(e) {
    const { type, channelState, isReconnecting } = e.data;
    
    switch (type) {
      case 'START':
        console.log('[Worker] Received START command');
        startHeartbeat();
        break;
        
      case 'STOP':
        console.log('[Worker] Received STOP command');
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
          heartbeatInterval = null;
        }
        break;
        
      case 'PONG':
        // Main thread responded to our ping
        lastPongTime = Date.now();
        missedPongs = 0;
        
        // Check if channel state is unhealthy
        if (channelState && channelState !== 'joined' && channelState !== 'joining' && !isReconnecting) {
          console.log('[Worker] Channel state unhealthy: ' + channelState);
          self.postMessage({ type: 'RECONNECT_NEEDED', reason: 'CHANNEL_UNHEALTHY', channelState: channelState });
        }
        break;
        
      case 'RESET':
        // Reset missed pongs counter (e.g., after successful reconnect)
        console.log('[Worker] Received RESET command');
        missedPongs = 0;
        lastPongTime = Date.now();
        break;
        
      default:
        console.log('[Worker] Unknown message type: ' + type);
    }
  };

  console.log('[Worker] Connection health worker initialized');
`;

/**
 * Creates a new connection health Web Worker
 * @returns {Worker|null} The worker instance or null if Web Workers are not supported
 */
export function createConnectionWorker() {
  // Check if Web Workers are supported
  if (typeof Worker === 'undefined') {
    console.warn('Web Workers not supported in this browser');
    return null;
  }

  try {
    // Create a Blob from the worker code
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    
    // Clean up the blob URL after worker is created
    // (worker has already loaded the code)
    URL.revokeObjectURL(workerUrl);
    
    console.log('[Main] Connection health worker created');
    return worker;
  } catch (error) {
    console.error('[Main] Failed to create connection worker:', error);
    return null;
  }
}

/**
 * Helper to safely terminate a worker
 * @param {Worker|null} worker 
 */
export function terminateWorker(worker) {
  if (worker) {
    worker.postMessage({ type: 'STOP' });
    worker.terminate();
    console.log('[Main] Connection health worker terminated');
  }
}

